// ============================================
// BORRADORFORMULARIO.JS — red de seguridad de CUALQUIER formulario de la app
// ============================================
// TANDA 2 (2026-09-14): antes era `borradorExpediente.js` y solo servia al modal de expediente.
// Se generaliza para ser la UNICA fuente del borrador en toda la app (no se reimplementa por
// pantalla): la clave pasa a ser usuario + PANTALLA + registro, para que no se mezclen borradores
// de distintas pantallas ni de distintos registros.
// MOVIL-GUARDAR (2026-09-12): Ricardo perdio trabajo capturando un expediente en el celular
// porque no alcanzaba el boton Guardar. Arreglada la causa, esto es el cinturon de seguridad:
// mientras se captura, lo escrito se guarda en localStorage; si la ventana se cierra o el
// navegador se recarga, al volver a abrir se ofrece recuperarlo.
//
// Se guarda POR USUARIO y POR EXPEDIENTE (clave "<usuarioId>:<expedienteId|nuevo>") para que dos
// personas en el mismo equipo, o dos expedientes distintos, no se pisen el borrador.
// Nunca guarda dinero ni nada calculado: solo lo que la persona escribio en el formulario.

import React from 'react';
import { storage, STORAGE_KEYS } from './storage';

// Un borrador viejo no sirve de nada y solo estorba: se descarta pasado este tiempo.
const VIGENCIA_MS = 7 * 24 * 60 * 60 * 1000; // 7 dias

// Quien esta capturando. Si la pantalla no lo pasa, se lee de la sesion: asi NINGUNA pantalla
// puede olvidarlo y acabar guardando todo bajo 'anon', que en un equipo compartido significa que
// una persona veria el borrador de otra (hallazgo de Dona Frida, 2026-09-14).
function usuarioActual() {
  try {
    const u = storage.get(STORAGE_KEYS.USER_DATA);
    return (u && u.id) ? u.id : null;
  } catch { return null; }
}

const clave = (usuarioId, pantalla, registroId) => {
  const quien = usuarioId != null ? usuarioId : usuarioActual();
  return `${quien || 'anon'}:${pantalla || 'general'}:${registroId || 'nuevo'}`;
};

function leerTodos() {
  try {
    const v = storage.get(STORAGE_KEYS.EXPEDIENTE_DRAFT);
    return (v && typeof v === 'object') ? v : {};
  } catch { return {}; }
}

function escribirTodos(mapa) {
  try { storage.set(STORAGE_KEYS.EXPEDIENTE_DRAFT, mapa); } catch { /* sin espacio o modo privado: ni modo */ }
}

// Guarda lo capturado. `datos` debe ser un objeto plano y serializable.
export function guardarBorrador(usuarioId, pantalla, registroId, datos) {
  if (!datos || typeof datos !== 'object') return;
  const mapa = leerTodos();
  mapa[clave(usuarioId, pantalla, registroId)] = { datos, guardadoEn: Date.now() };
  // Aprovechar para barrer los vencidos: el almacenamiento del navegador es chico.
  const ahora = Date.now();
  for (const k of Object.keys(mapa)) {
    if (!mapa[k] || ahora - (mapa[k].guardadoEn || 0) > VIGENCIA_MS) delete mapa[k];
  }
  escribirTodos(mapa);
}

// Devuelve { datos, guardadoEn } o null si no hay (o si ya vencio).
export function leerBorrador(usuarioId, pantalla, registroId) {
  const e = leerTodos()[clave(usuarioId, pantalla, registroId)];
  if (!e || !e.datos) return null;
  if (Date.now() - (e.guardadoEn || 0) > VIGENCIA_MS) return null;
  return e;
}

export function borrarBorrador(usuarioId, pantalla, registroId) {
  const mapa = leerTodos();
  delete mapa[clave(usuarioId, pantalla, registroId)];
  escribirTodos(mapa);
}

// ¿El borrador aporta algo distinto de lo que ya trae el servidor? Si es igual, no se
// molesta a la persona ofreciendole "recuperar" algo identico a lo que ya ve en pantalla.
export function borradorDifiere(borrador, valoresActuales) {
  if (!borrador || !borrador.datos) return false;
  const d = borrador.datos;
  return Object.keys(d).some(k => {
    const a = d[k] == null ? '' : String(d[k]);
    const b = valoresActuales && valoresActuales[k] != null ? String(valoresActuales[k]) : '';
    return a !== b;
  });
}

// Texto humano del "hace cuanto" para el aviso de recuperacion.
export function haceCuanto(ts) {
  const min = Math.floor((Date.now() - ts) / 60000);
  if (min < 1) return 'hace un momento';
  if (min < 60) return `hace ${min} minuto${min === 1 ? '' : 's'}`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h} hora${h === 1 ? '' : 's'}`;
  const d = Math.floor(h / 24);
  return `hace ${d} dia${d === 1 ? '' : 's'}`;
}

// ============================================
// HOOK REUTILIZABLE — para no reimplementar el borrador en cada pantalla (TANDA 2)
// ============================================
// Uso tipico:
//   const bor = useBorrador('clientes', clienteId, form, setForm, { activo: !bloqueado, hayCambios });
//   ...  {bor.pendiente && <AvisoRescate {...bor} />}   // el aviso lo pinta cada pantalla a su estilo
//   ...  onClose={bor.confirmarCierre(onCerrar)}        // pregunta antes de descartar
//   ...  al guardar bien: bor.limpiar()

export function useBorrador(pantalla, registroId, form, setForm, opciones = {}) {
  const { usuarioId = null, activo = true, retraso = 600, omitir = [] } = opciones;
  const [pendiente, setPendiente] = React.useState(null);

  // Referencia de como ABRIO el formulario. Se congela en el primer render: es contra esto que se
  // decide si de verdad hay algo capturado (los selectores con valor por defecto no cuentan).
  const inicialRef = React.useRef(null);
  if (inicialRef.current === null) inicialRef.current = opciones.iniciales || form || {};

  const hayCambios = opciones.hayCambios !== undefined
    ? opciones.hayCambios
    : Object.keys(form || {}).some(k => {
      const a = form[k] == null ? '' : String(form[k]);
      const b = inicialRef.current[k] == null ? '' : String(inicialRef.current[k]);
      return a !== b;
    });

  // Al abrir: si hay algo guardado y difiere de lo que trae el servidor, se ofrece rescatarlo.
  React.useEffect(() => {
    if (!activo) return;
    const b = leerBorrador(usuarioId, pantalla, registroId);
    if (b && borradorDifiere(b, inicialRef.current)) setPendiente(b);
    // solo al abrir: recalcularlo con cada tecleo haria reaparecer el aviso sin parar
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pantalla, registroId, usuarioId, activo]);

  // Mientras se captura: se guarda con un respiro, para no escribir en cada tecla.
  React.useEffect(() => {
    if (!activo || !hayCambios) return undefined;
    const t = setTimeout(() => {
      // `omitir`: campos que NO deben ir al almacenamiento del navegador (imagenes en base64,
      // que pesan megas y reventarian la cuota; ids y banderas que no son captura de la persona).
      const datos = {};
      for (const k of Object.keys(form || {})) if (!omitir.includes(k)) datos[k] = form[k];
      guardarBorrador(usuarioId, pantalla, registroId, datos);
    }, retraso);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, hayCambios, activo, usuarioId, pantalla, registroId, retraso]);

  const recuperar = React.useCallback(() => {
    setPendiente(p => {
      if (p && p.datos && typeof setForm === 'function') setForm(prev => ({ ...prev, ...p.datos }));
      return null;
    });
  }, [setForm]);

  const descartar = React.useCallback(() => {
    borrarBorrador(usuarioId, pantalla, registroId);
    setPendiente(null);
  }, [usuarioId, pantalla, registroId]);

  const limpiar = React.useCallback(() => {
    borrarBorrador(usuarioId, pantalla, registroId);
  }, [usuarioId, pantalla, registroId]);

  // Envuelve el cierre para NO descartar en silencio lo capturado.
  const confirmarCierre = React.useCallback((cerrar) => () => {
    if (hayCambios) {
      const aviso = [
        'Tienes cambios sin guardar. Si cierras ahora no se aplicaran.',
        'Lo capturado queda guardado en este dispositivo y podras recuperarlo al volver a abrir.',
        '¿Cerrar de todos modos?',
      ].join(String.fromCharCode(10, 10));
      if (!window.confirm(aviso)) return;
    }
    if (typeof cerrar === 'function') cerrar();
  }, [hayCambios]);

  return { pendiente, recuperar, descartar, limpiar, confirmarCierre, haceCuanto };
}

// Variante para pantallas cuyo formulario NO vive en un objeto `form` sino en muchos useState
// sueltos (Tareas, Clientes, MiAgenda...). Evita refactorizarlas: se les pide solo
//   `campos`  = objeto plano armado con esos estados
//   `aplicar` = funcion que recibe el objeto guardado y llama a cada setter
// Todo lo demas (guardado con respiro, rescate, confirmacion al cerrar) es identico.
export function useBorradorCampos(pantalla, registroId, campos, aplicar, opciones = {}) {
  const { usuarioId = null, activo = true, retraso = 600, iniciales = null } = opciones;
  const [pendiente, setPendiente] = React.useState(null);
  // hayCambios: si la pantalla declara sus valores INICIALES (lo normal cuando hay selectores con
  // default: tipo 'normal', prioridad 'media'...), hay cambios solo cuando algo DIFIERE de ellos.
  // Sin `iniciales`, el criterio simple: que algun campo tenga algo escrito.
  const hayCambios = opciones.hayCambios !== undefined
    ? opciones.hayCambios
    : iniciales
      ? Object.keys(campos || {}).some(k => {
        const a = campos[k] == null ? '' : String(campos[k]);
        const b = iniciales[k] == null ? '' : String(iniciales[k]);
        return a !== b;
      })
      : Object.values(campos || {}).some(v => v != null && v !== '' && !(Array.isArray(v) && !v.length));

  React.useEffect(() => {
    if (!activo) return;
    const b = leerBorrador(usuarioId, pantalla, registroId);
    // Solo se ofrece rescate si lo guardado APORTA algo sobre el formulario recien abierto.
    if (b && b.datos && Object.keys(b.datos).length && borradorDifiere(b, iniciales || {})) setPendiente(b);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pantalla, registroId, usuarioId, activo]);

  React.useEffect(() => {
    if (!activo || !hayCambios) return undefined;
    const t = setTimeout(() => guardarBorrador(usuarioId, pantalla, registroId, campos), retraso);
    return () => clearTimeout(t);
  }, [campos, hayCambios, activo, usuarioId, pantalla, registroId, retraso]);

  const recuperar = React.useCallback(() => {
    setPendiente(p => {
      if (p && p.datos && typeof aplicar === 'function') aplicar(p.datos);
      return null;
    });
  }, [aplicar]);

  const descartar = React.useCallback(() => {
    borrarBorrador(usuarioId, pantalla, registroId);
    setPendiente(null);
  }, [usuarioId, pantalla, registroId]);

  const limpiar = React.useCallback(() => borrarBorrador(usuarioId, pantalla, registroId), [usuarioId, pantalla, registroId]);

  const confirmarCierre = React.useCallback((cerrar) => () => {
    if (hayCambios) {
      const aviso = [
        'Tienes cambios sin guardar. Si cierras ahora no se aplicaran.',
        'Lo capturado queda guardado en este dispositivo y podras recuperarlo al volver a abrir.',
        '¿Cerrar de todos modos?',
      ].join(String.fromCharCode(10, 10));
      if (!window.confirm(aviso)) return;
    }
    if (typeof cerrar === 'function') cerrar();
  }, [hayCambios]);

  return { pendiente, recuperar, descartar, limpiar, confirmarCierre, haceCuanto };
}

// Aviso de rescate, listo para usar. Cada pantalla solo lo coloca donde quiera.
export function AvisoRescate({ bor }) {
  if (!bor || !bor.pendiente) return null;
  return React.createElement('div', {
    style: {
      background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af',
      padding: '12px 14px', borderRadius: 10, fontSize: 13, lineHeight: 1.5, marginBottom: 12,
    },
  }, [
    React.createElement('div', { key: 't', style: { fontWeight: 700, marginBottom: 6 } },
      `Tenias algo capturado sin guardar (${haceCuanto(bor.pendiente.guardadoEn)})`),
    React.createElement('div', { key: 'd', style: { marginBottom: 10 } },
      'Se quedo en este dispositivo. ¿Lo recuperamos?'),
    React.createElement('div', { key: 'b', style: { display: 'flex', gap: 8, flexWrap: 'wrap' } }, [
      React.createElement('button', {
        key: 'r', type: 'button', onClick: bor.recuperar,
        style: {
          background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 8,
          padding: '10px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer', minHeight: 44,
        },
      }, 'Recuperar lo que estaba capturando'),
      React.createElement('button', {
        key: 'x', type: 'button', onClick: bor.descartar,
        style: {
          background: '#fff', color: '#1e40af', border: '1px solid #bfdbfe', borderRadius: 8,
          padding: '10px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', minHeight: 44,
        },
      }, 'Descartar'),
    ]),
  ]);
}
